import { FC } from "react";
import { Flex, Grid, GridItem, Heading, Text } from "@smile2impress/components";
import { useTranslation } from "next-i18next";
import { PriceBlock as PriceBlockPackage } from "@smile2impress/price-block-next";
import { mergeDeepRight } from "ramda";
import {
    TreatmentCase,
    UTSLocale,
    Product,
} from "@smile2impress/attraction-constants";
import { useUTSLocaleContext } from "context/utsLocaleContext";

import { Button } from "components/Button";
import { PriceBanner } from "components/PromoCampaigns/components";

import pageContent from "./content";
import { PriceBlockProps } from "./PriceBlock.types";

export const PriceBlock: FC<PriceBlockProps> = ({
    products = [],
    cases,
    onCTAClick,
    prefix,
    contentOverride,
    hideCasesList = false,
    hideMonthlyPrice = false,
    hideDurationGraph = false,
    hideBiteCorrection = false,
    hideCaseFullName = false,
    promoButton = null,
    alwaysShowTnC = false,
}) => {
    const { t } = useTranslation("priceblock");
    const { locale } = useUTSLocaleContext();
    const { i18n } = useTranslation();

    const { title, description, cta } = contentOverride
        ? mergeDeepRight(pageContent(locale)(t), contentOverride)
        : pageContent(locale)(t);

    const promo = promoButton || <PriceBanner />;
    const disallowedCasesList = [
        "LightCase" as TreatmentCase,
        "whiteningPrice" as TreatmentCase,
        "teensRegularPrice" as TreatmentCase,
        "teensPlusPrice" as TreatmentCase,
        TreatmentCase.WHITENING,
        TreatmentCase.KIDS,
        TreatmentCase.TEENSREGULAR,
        TreatmentCase.TEENSPLUS,
        TreatmentCase.TEENSDIGITALBRACES,
        TreatmentCase.SUPERLIGHT,
        TreatmentCase.RETAINERSSUBSLIGHT,
        TreatmentCase.RETAINERSSUBSFULL,
        "kidsPrice" as TreatmentCase,
    ];
    const hideMonthlyPriceList: UTSLocale[] = [];
    const alwaysShowTnCList: UTSLocale[] = [UTSLocale.FRANCE_FR];

    return (
        <Grid
            as="section"
            gridTemplateColumns={["1fr", "1fr", "repeat(24, 1fr)"]}
            gridColumnGap={["initial", "initial", 2]}
            bgColor={["base.grey", "base.grey", "inherit"]}
            px={[2, 2, 0]}
            pt={[3, 3, 0]}
            borderRadius={["sm"]}
            overflow="hidden"
        >
            <GridItem
                colStart={["auto", "auto", 2]}
                colSpan={["auto", "auto", 10]}
            >
                <Flex
                    direction={"column"}
                    flex="1 1 0"
                    textAlign={["center", "center", "left"]}
                >
                    <Heading
                        textStyle="H2"
                        mb={description ? 1 : 0}
                        color="text.base"
                        _dark={{
                            color: [
                                "text.baseInverse",
                                "text.baseInverse",
                                "text.base",
                            ],
                        }}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                    {description && (
                        <Text
                            textStyle="body"
                            color="text.base"
                            _dark={{
                                color: [
                                    "text.baseInverse",
                                    "text.baseInverse",
                                    "text.base",
                                ],
                            }}
                        >
                            {description}
                        </Text>
                    )}
                </Flex>
            </GridItem>
            <GridItem
                colStart={["auto", "auto", 13]}
                colSpan={["auto", "auto", 11]}
            >
                <Flex flex="1 1 0" direction="column" mx={[-2, -2, 0]}>
                    <PriceBlockPackage
                        i18n={{ i18n }}
                        locale={locale}
                        products={products as Product[]}
                        prefix={prefix}
                        cases={cases}
                        hideCasesList={hideCasesList}
                        hideMonthlyPrice={
                            hideMonthlyPrice ||
                            hideMonthlyPriceList.includes(locale)
                        }
                        hideDurationGraph={hideDurationGraph}
                        hideBiteCorrection={hideBiteCorrection}
                        hideCaseFullName={hideCaseFullName}
                        {...(cta?.link && {
                            cta: (
                                <Button
                                    onClick={onCTAClick}
                                    {...cta}
                                    w="100%"
                                    as="span"
                                />
                            ),
                        })}
                        content={contentOverride}
                        alwaysShowTnC={
                            alwaysShowTnC || alwaysShowTnCList.includes(locale)
                        }
                        {...(promo && {
                            promoButton: {
                                component: promo,
                                disallowCases:
                                    disallowedCasesList as TreatmentCase[],
                            },
                        })}
                    />
                </Flex>
            </GridItem>
        </Grid>
    );
};
